import { Marked, type Tokens, type RendererThis } from 'marked';

export interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

/** A stable, readable anchor for a heading. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    // Turkish 'İ' lowercases to 'i' plus a combining dot, and the mark is not
    // in the range below — without this, "İmaj kaydı" anchors as "i-maj-kaydı".
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[`*_]/g, '')
    .replace(/[^a-z0-9À-ɏ]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Strips inline markup so a heading reads plainly in the table of contents. */
function plain(markdown: string): string {
  return markdown
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .trim();
}

/**
 * Renders a document's Markdown to HTML and, in the same pass, collects its
 * table of contents.
 *
 * The content is the repository's own file, compiled into the bundle — not
 * anything a visitor can supply — so this deliberately does not carry a
 * sanitiser it would only pay for on every page load.
 */
export function renderDoc(markdown: string): { html: string; toc: TocEntry[] } {
  const toc: TocEntry[] = [];
  const seen = new Map<string, number>();

  const marked = new Marked({ gfm: true, breaks: false });
  marked.use({
    renderer: {
      heading(this: RendererThis, token: Tokens.Heading) {
        const { tokens, depth, text } = token;
        const label = plain(text);
        let id = slugify(label);
        // Two sections can legitimately share a title ("Configuring it"); an
        // anchor that jumps to the wrong one is worse than an ugly suffix.
        const n = seen.get(id) ?? 0;
        seen.set(id, n + 1);
        if (n > 0) id = `${id}-${n + 1}`;

        if (depth === 2 || depth === 3) toc.push({ id, text: label, level: depth as 2 | 3 });
        return `<h${depth} id="${id}"><a class="anchor" href="#${id}" aria-label="Link to this section">#</a>${this.parser.parseInline(
          tokens,
        )}</h${depth}>\n`;
      },
      // External links open in a new tab; in-page anchors must not.
      link(this: RendererThis, token: Tokens.Link) {
        const { href, title, tokens } = token;
        const external = /^https?:\/\//.test(href);
        const attrs = [
          `href="${href}"`,
          title ? `title="${title}"` : '',
          external ? 'target="_blank" rel="noopener noreferrer"' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return `<a ${attrs}>${this.parser.parseInline(tokens)}</a>`;
      },
    },
  });

  const html = marked.parse(markdown, { async: false }) as string;
  return { html, toc };
}
